// src/app/components/sessions/SesionCamaraUI.tsx
// Versión ajustada a las convenciones del repo (semantic tokens).
// Diferencias vs v1-raw:
//   - hex hardcoded  → tokens Tailwind (bg-background, bg-card, border-border,
//     text-muted-foreground, bg-primary, text-primary-foreground, bg-muted, etc.)
//   - bg-[#003B4A]/8 → bg-primary/10  (en Tailwind default no existe /8)
//   - quité import de forwardRef sin usar
//   - quité fontVariantNumeric inline (ya lo hace la clase tabular-nums)
import { useEffect, useState, type ReactNode, type Ref } from "react"
import {
  AlertCircle,
  ArrowLeft,
  Bug,
  CheckCircle2,
  Loader2,
  PartyPopper,
  Play,
  RefreshCw,
  Trophy,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  XCircle,
} from "lucide-react"
import { CountdownOverlay } from "./CountdownOverlay"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FeedbackItem {
  id: string
  message: string
  passed: boolean
}

export type LoadingState = "ready" | "loading" | "permission-error"

/** Estado del ciclo de una serie. El smart component lo controla:
 *  idle               — paciente todavía no arrancó la serie (overlay con botón "Empezar").
 *  calibracion        — capturando baseline (2s parado quieto) para schemas que lo requieren.
 *                       Solo schemas con baseline (sentadilla). Skipea para rot externa.
 *  calibracion_fallida — gate del baseline falló 3+ veces, ofrecemos cancelar.
 *  counting           — countdown 3-2-1 antes de que el detector arranque.
 *  active             — UI normal, detector recibe frames y cuenta reps.
 *  rest               — serie terminada, esperando click "Próxima serie".
 *  finished           — última serie terminada, esperando click "Finalizar". */
export type SerieMode =
  | "idle"
  | "calibracion"
  | "calibracion_fallida"
  | "counting"
  | "active"
  | "rest"
  | "finished"

export interface DebugValues {
  [key: string]: string | number
}

interface SesionCamaraUIProps {
  videoRef: Ref<HTMLVideoElement>
  canvasRef: Ref<HTMLCanvasElement>
  /** Ref al contenedor del video+canvas — usado por el smart para
   *  medir dimensiones al setear canvas.width/height con DPR y para
   *  computar el rect visible del video (object-contain/cover). */
  containerRef?: Ref<HTMLDivElement>

  ejercicioNombre: string

  repCount: number
  repsObjetivo: number
  serieActual: number
  seriesObjetivo: number

  feedback: FeedbackItem[]
  fps: number

  debugMode: boolean
  /** Valores crudos del motor de detección para el panel debug. Opcional. */
  debugValues?: DebugValues
  /** Modelo activo (para mostrar en el chip FPS). Ej: "MediaPipe Pose Lite". */
  modeloNombre?: string

  loadingState?: LoadingState

  /** Estado del ciclo de la serie. Controla qué overlay se muestra. */
  mode: SerieMode
  /** Valor a mostrar durante el countdown (3, 2, 1). null cuando mode !== 'counting'. */
  countdownValue: number | null
  /** Progreso de captura del baseline (0..1). Solo relevante cuando mode === 'calibracion'. */
  calibracionProgreso?: number
  /** Cantidad de intentos fallidos previos de la captura (0, 1, 2, 3...).
   *  La UI escala el copy según el contador. */
  calibracionIntentos?: number

  onToggleDebug: () => void
  /** Estado del mute del audio-feedback (persistido en localStorage). */
  audioMuted: boolean
  onToggleMuted: () => void
  onSalir: () => void
  /** Llamado cuando el paciente clickea "Empezar serie" (modo idle) o
   *  "Próxima serie" (modo rest). El smart decide si hay que reset evaluator. */
  onStartSerie: () => void
  /** Llamado cuando el paciente clickea "Cancelar sesión" en el modal de
   *  calibracion_fallida (3+ retries sin gate OK). Cancela la sesión. */
  onCancelarCalibracion?: () => void
  /** Llamado cuando el paciente clickea "Finalizar sesión" en el modal de
   *  cierre. El smart navega a la pantalla de resumen. Solo se habilita
   *  cuando guardado === true (sesión persistida en Supabase). */
  onFinalizar: () => void
  onRetryPermission?: () => void

  // ── Estado de persistencia de la sesión completada ────────────────────────
  /** RPC en vuelo. */
  guardando: boolean
  /** RPC retornó OK. */
  guardado: boolean
  /** RPC retornó error. null si no hubo error o todavía no completó. */
  errorGuardar: Error | null
  /** Disparado por el botón "Reintentar" del modal de error. */
  onReintentar: () => void

  /** Slot opcional renderizado dentro del area de cámara para overlays
   *  (ej. DetectorDebugHUD). El parent controla cuándo mostrarlo. */
  cameraOverlay?: ReactNode
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SesionCamaraUI({
  videoRef,
  canvasRef,
  containerRef,
  ejercicioNombre,
  repCount,
  repsObjetivo,
  serieActual,
  seriesObjetivo,
  feedback,
  fps,
  debugMode,
  debugValues = {},
  modeloNombre = "MediaPipe Pose Lite",
  loadingState = "ready",
  mode,
  countdownValue,
  calibracionProgreso = 0,
  calibracionIntentos = 0,
  onToggleDebug,
  audioMuted,
  onToggleMuted,
  onSalir,
  onStartSerie,
  onCancelarCalibracion,
  onFinalizar,
  onRetryPermission,
  guardando,
  guardado,
  errorGuardar,
  onReintentar,
  cameraOverlay,
}: SesionCamaraUIProps) {
  const pct = Math.min(100, Math.round((repCount / Math.max(1, repsObjetivo)) * 100))

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      {/* ── Header ── */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border bg-card">
        <button
          onClick={onSalir}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors rounded-full px-3 py-2 hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
          Salir
        </button>

        <h1 className="text-sm sm:text-base font-bold text-primary truncate max-w-[50%]">
          {ejercicioNombre}
        </h1>

        <div className="flex items-center gap-2">
          <button
            onClick={onToggleMuted}
            aria-label={audioMuted ? "Activar audio" : "Silenciar audio"}
            title={audioMuted ? "Audio silenciado — click para activar" : "Audio activo — click para silenciar"}
            className={`inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors
              ${
                audioMuted
                  ? "bg-muted text-muted-foreground hover:bg-border"
                  : "bg-primary/10 text-primary hover:bg-primary/20"
              }`}
          >
            {audioMuted ? (
              <VolumeX className="h-4 w-4" strokeWidth={2} />
            ) : (
              <Volume2 className="h-4 w-4" strokeWidth={2} />
            )}
          </button>
          {/* Debug solo en dev local: online (showcase) no se muestra. */}
          {import.meta.env.DEV && (
          <button
            onClick={onToggleDebug}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-2 transition-colors
              ${
                debugMode
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-border"
              }`}
          >
            <Bug className="h-3.5 w-3.5" />
            Debug
          </button>
          )}
        </div>
      </header>

      {/* ── Main grid ──
          Mobile: flex-col vertical stack (cámara arriba, panel abajo).
          Desktop lg: grid 2-col con sidebar fija 360px + cámara grande que
          llena viewport height. object-contain preserva aspect ratio real
          del stream (no crop → no oculta keypoints). Cap 800px sobre la
          cámara para viewports 4K, resto es padding negro.
          --header-h queda hardcoded a 60px (barra propia py-3 + text-base). */}
      <main className="flex-1 p-4 sm:p-6 gap-4 flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6 lg:h-[calc(100vh-60px)] lg:overflow-hidden">
        {/* ─ Camera zone ─ */}
        <section className="flex-1 min-w-0 lg:h-full lg:min-h-0">
          <div
            ref={containerRef}
            className="relative rounded-2xl overflow-hidden bg-black shadow-sm aspect-video lg:aspect-auto lg:h-full lg:max-h-[800px]"
          >
            {/* Video element (ref forwarded from parent — parent attaches MediaStream).
                object-cover en mobile (viewport portrait tolerante al crop),
                object-contain en desktop (letterbox negro, sin distorsión).
                TODO: si se implementa drawing de skeleton en el canvas, el
                modo contain requiere recomputar bounding box efectivo del
                stream visible dentro del canvas (letterbox no dibujable). */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover lg:object-contain"
            />

            {/* Keypoint overlay canvas — parent draws skeleton here */}
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full pointer-events-none"
            />

            {/* Feedback badges (top-left) */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5 max-w-[70%]">
              {feedback.map((f) => (
                <FeedbackBadge key={f.id} item={f} />
              ))}
            </div>

            {/* FPS / model chip (top-right) */}
            {debugMode && (
              <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[11px] font-mono px-2.5 py-1 rounded-full">
                {fps} FPS · {modeloNombre}
              </div>
            )}

            {/* Loading overlay */}
            {loadingState === "loading" && <LoadingOverlay />}

            {/* Permission error overlay */}
            {loadingState === "permission-error" && (
              <PermissionErrorOverlay onRetry={onRetryPermission} />
            )}

            {/* Camera-anchored overlay slot (DetectorDebugHUD, future skeleton, etc.) */}
            {cameraOverlay && (
              <div className="absolute inset-0 pointer-events-none">
                {cameraOverlay}
              </div>
            )}
          </div>
        </section>

        {/* ─ Side panel ─
            Mobile: stack normal debajo de la cámara.
            Desktop: columna derecha fija 360px, scroll interno si el
            contenido excede la altura viewport disponible. */}
        <aside className="flex flex-col gap-3 min-w-[260px] lg:min-w-0 lg:h-full lg:overflow-y-auto lg:pr-1">
          {/* Main counter card — número gigante en desktop para impacto visual. */}
          <div className="rounded-2xl bg-card border border-border shadow-sm p-5 lg:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              Repeticiones
            </p>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-primary tabular-nums leading-none">
                {repCount}
              </span>
              <span className="text-2xl lg:text-3xl font-semibold text-muted-foreground tabular-nums">
                /{repsObjetivo}
              </span>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Series card */}
          <div className="rounded-2xl bg-card border border-border shadow-sm p-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Serie
              </p>
              <p className="text-lg font-bold text-primary mt-0.5">
                {serieActual}{" "}
                <span className="text-muted-foreground font-normal">
                  de {seriesObjetivo}
                </span>
              </p>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: seriesObjetivo }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 w-6 rounded-full ${
                    i < serieActual - 1
                      ? "bg-primary"
                      : i === serieActual - 1
                        ? "bg-primary/50"
                        : "bg-border"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Debug terminal panel */}
          {debugMode && (
            <DebugPanel values={debugValues} fps={fps} modelo={modeloNombre} />
          )}

          {/* Feedback recap (below counters) — visible only on desktop for extra context */}
          <div className="hidden lg:block rounded-2xl bg-card border border-border shadow-sm p-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              Forma
            </p>
            {feedback.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {repCount > 0 ? "Buena forma, seguí así" : "Analizando tu movimiento..."}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {feedback.map((f) => (
                  <li key={f.id} className="flex items-start gap-2 text-sm">
                    {f.passed ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                    )}
                    <span
                      className={
                        f.passed
                          ? "text-primary"
                          : "text-red-700 font-medium"
                      }
                    >
                      {f.message}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </main>

      {/* ── Overlays según mode ── */}
      {mode === "idle" && (
        <CelebrationModal
          icon={<Play className="h-10 w-10 text-primary" />}
          title="Listo para arrancar"
          subtitle="Posicioná tu cámara y tu cuerpo, después tocá Empezar. Vas a tener 3 segundos para ubicarte antes de que empiece a contar."
          ctaLabel={`Empezar serie ${serieActual} de ${seriesObjetivo}`}
          ctaVariant="primary"
          onCta={onStartSerie}
        />
      )}
      {mode === "calibracion" && (
        <CalibracionOverlay
          progreso={calibracionProgreso}
          intentos={calibracionIntentos}
        />
      )}
      {mode === "calibracion_fallida" && (
        <CalibracionFallidaModal
          onCancelar={onCancelarCalibracion ?? onSalir}
        />
      )}
      {mode === "counting" && countdownValue !== null && (
        <CountdownOverlay value={countdownValue} />
      )}
      {mode === "rest" && (
        <CelebrationModal
          icon={<PartyPopper className="h-10 w-10 text-primary" />}
          title={`¡Serie ${serieActual} lista!`}
          subtitle={`Completaste ${repsObjetivo} repeticiones. Tomate unos segundos antes de seguir.`}
          ctaLabel="Próxima serie"
          ctaVariant="primary"
          onCta={onStartSerie}
        />
      )}
      {mode === "finished" && (
        <FinishedModal
          seriesObjetivo={seriesObjetivo}
          guardando={guardando}
          guardado={guardado}
          errorGuardar={errorGuardar}
          onFinalizar={onFinalizar}
          onReintentar={onReintentar}
        />
      )}
    </div>
  )
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function FeedbackBadge({ item }: { item: FeedbackItem }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [item.id])

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur-sm transition-all duration-200
        ${
          item.passed
            ? "bg-green-500/90 text-white"
            : "bg-red-500/90 text-white"
        }
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"}
      `}
    >
      {item.passed ? (
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
      ) : (
        <XCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
      )}
      <span>{item.message}</span>
    </div>
  )
}

function LoadingOverlay() {
  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-white">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="text-sm font-medium">Cargando cámara...</p>
    </div>
  )
}

function PermissionErrorOverlay({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-6">
      <div className="rounded-2xl bg-card p-6 max-w-sm w-full text-center shadow-lg">
        <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
          <VideoOff className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="font-bold text-primary text-lg">
          No pudimos usar tu cámara
        </h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Revisá los permisos del navegador y volvé a intentar. Sin cámara no
          podemos medir tu ejercicio.
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold text-sm px-5 py-3 hover:bg-primary/90 transition-colors"
          >
            <Video className="h-4 w-4" />
            Reintentar
          </button>
        )}
      </div>
    </div>
  )
}

function DebugPanel({
  values,
  fps,
  modelo,
}: {
  values: DebugValues
  fps: number
  modelo: string
}) {
  const entries = Object.entries(values)
  const sample: DebugValues = entries.length
    ? values
    : {
        angulo_codo: "91°",
        distancia_codo_cadera: 0.06,
        fase: "concentrica",
        confianza_keypoints: 0.94,
        tiempo_fase_ms: 420,
      }
  return (
    <div className="rounded-2xl bg-black border border-zinc-800 shadow-sm p-3 font-mono text-[11px] leading-relaxed text-green-400 overflow-hidden">
      <div className="flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-wider border-b border-zinc-800 pb-2 mb-2">
        <span>debug.live</span>
        <span>{fps} fps</span>
      </div>
      <p className="text-zinc-500">$ {modelo}</p>
      {Object.entries(sample).map(([k, v]) => (
        <p key={k}>
          <span className="text-zinc-500">{k}:</span>{" "}
          <span>{String(v)}</span>
        </p>
      ))}
      <p className="text-green-500/70 mt-1">
        <span className="animate-pulse">▋</span>
      </p>
    </div>
  )
}

/** Modal del estado finished. Branchea sobre el estado de la RPC de
 *  persistencia: guardando → spinner, error → reintentar, ok → CTA finalizar.
 *  Si todavía no arrancó la RPC (entre setMode('finished') y el useEffect
 *  del smart) caemos al estado guardando — UX uniforme. */
function FinishedModal({
  seriesObjetivo,
  guardando,
  guardado,
  errorGuardar,
  onFinalizar,
  onReintentar,
}: {
  seriesObjetivo: number
  guardando: boolean
  guardado: boolean
  errorGuardar: Error | null
  onFinalizar: () => void
  onReintentar: () => void
}) {
  if (errorGuardar && !guardado) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
        <div className="rounded-2xl bg-card shadow-xl max-w-sm w-full p-6 text-center">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-primary">
            No pudimos guardar tu sesión
          </h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Hubo un problema al subir el resumen al servidor. Probá de nuevo.
          </p>
          <p className="text-xs text-muted-foreground mt-2 break-words">
            {errorGuardar.message}
          </p>
          <button
            onClick={onReintentar}
            className="mt-5 w-full h-14 rounded-full font-semibold text-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-colors shadow-sm inline-flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (guardado) {
    return (
      <CelebrationModal
        icon={<Trophy className="h-10 w-10 text-green-600" />}
        title="¡Sesión completada!"
        subtitle={`Hiciste las ${seriesObjetivo} series. Tu kinesiólogo va a ver el resumen de esta sesión.`}
        ctaLabel="Finalizar sesión"
        ctaVariant="success"
        onCta={onFinalizar}
      />
    )
  }

  // guardando, o estado intermedio antes de que el effect dispare la RPC.
  void guardando
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="rounded-2xl bg-card shadow-xl max-w-sm w-full p-6 text-center">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
        </div>
        <h2 className="text-xl font-bold text-primary">
          Guardando tu sesión…
        </h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Estamos subiendo el resumen al servidor. No cierres la app.
        </p>
      </div>
    </div>
  )
}

/** Overlay del estado calibracion. Mensaje guía + barra horizontal que se
 *  llena durante el `baseline.durationMs` (típicamente 2s). El copy escala
 *  según `intentos` previos fallidos para guiar al paciente.
 *
 *  La cámara sigue visible debajo del overlay (semi-transparente) — el
 *  paciente se ve a sí mismo y puede corregir postura en tiempo real. */
function CalibracionOverlay({
  progreso,
  intentos,
}: {
  progreso: number
  intentos: number
}) {
  const titulo =
    intentos === 0
      ? "Calibrando…"
      : intentos === 1
        ? "Probemos otra vez"
        : "Último intento"
  const mensaje =
    intentos === 0
      ? "Quedate quieto y parado derecho mientras te calibramos."
      : intentos === 1
        ? "No nos salió. Verificá que estés de costado y bien iluminado."
        : "Asegurate de que se te vea todo el cuerpo de costado, sin obstáculos."
  const pct = Math.max(0, Math.min(100, Math.round(progreso * 100)))
  return (
    // backdrop-blur-xs (4px en Tailwind v4) y bg-black/20 son intencionalmente
    // suaves: el paciente necesita ver su propia silueta para auto-corregirse
    // la postura durante la calibración. Suficiente blur para no detallar
    // features faciales / tatuajes, suficiente transparencia para ver pose.
    <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200 pointer-events-none">
      <div className="rounded-2xl bg-card shadow-xl max-w-sm w-full p-6 text-center">
        <h2 className="text-xl font-bold text-primary">{titulo}</h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          {mensaje}
        </p>
        <div
          className="mt-5 h-2 w-full rounded-full bg-muted overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-primary transition-[width] duration-150 ease-linear"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  )
}

/** Modal terminal: gate falló 3 veces consecutivas. No ofrecemos skip
 *  (baseline es importante para sentadilla), solo cancelar. */
function CalibracionFallidaModal({ onCancelar }: { onCancelar: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="rounded-2xl bg-card shadow-xl max-w-sm w-full p-6 text-center">
        <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="h-10 w-10 text-red-600" />
        </div>
        <h2 className="text-xl font-bold text-primary">
          No pudimos calibrar
        </h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Intentalo en otro momento con mejor luz y asegurándote de que se te
          vea todo el cuerpo de costado.
        </p>
        <button
          onClick={onCancelar}
          className="mt-5 w-full h-14 rounded-full font-semibold text-sm transition-colors shadow-sm bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          Cancelar sesión
        </button>
      </div>
    </div>
  )
}

function CelebrationModal({
  icon,
  title,
  subtitle,
  ctaLabel,
  ctaVariant,
  onCta,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  ctaLabel: string
  ctaVariant: "primary" | "success"
  onCta: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="rounded-2xl bg-card shadow-xl max-w-sm w-full p-6 text-center">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          {icon}
        </div>
        <h2 className="text-xl font-bold text-primary">{title}</h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          {subtitle}
        </p>
        <button
          onClick={onCta}
          className={`mt-5 w-full h-14 rounded-full font-semibold text-sm transition-colors shadow-sm
            ${
              ctaVariant === "success"
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-primary hover:bg-primary/90 text-primary-foreground"
            }`}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  )
}

export default SesionCamaraUI
